import { type FC, useEffect, useMemo, useRef, useState } from "react";

import { keepPreviousData } from "@tanstack/react-query";
import { useHistory } from "react-router";

import { useFullTextAssetSearchInfinite } from "~/v2/api/hooks/useGetAssetsFullTextSearch";
import { useGetQuotes } from "~/v2/api/hooks/useGetQuotes";
import { useMarketDataFeed } from "~/v2/api/hooks/useMarketDataFeed";
import type { Quote } from "~/v2/api/rest/data";
import { Path } from "~/v2/path";
import { AssetSearchPresenter } from "./AssetSearch.pres";
import { getAssetQuotePricing } from "./AssetSearch.utils";

interface AssetSearchConnectorProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onSelect?: (symbol: string) => void;
  autofocus?: boolean;
  showFilters?: boolean;
}

const TAB_CONFIG = [
  { id: "all", label: "All", assetClass: undefined },
  { id: "equities", label: "Equities", assetClass: "us_equity" },
  { id: "crypto", label: "Crypto", assetClass: "crypto" },
] as const;

// Filter to only show these asset classes in search results
const ALLOWED_ASSET_CLASSES = ["us_equity", "crypto"];

type TabId = (typeof TAB_CONFIG)[number]["id"];

export const AssetSearch: FC<AssetSearchConnectorProps> = ({
  placeholder = "Search by symbol...",
  value,
  onChange,
  onSelect,
  autofocus,
  showFilters = true,
}) => {
  const history = useHistory();
  const [internalQuery, setInternalQuery] = useState<string>("");
  const isControlled = value !== undefined;
  const query = isControlled ? value : internalQuery;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<TabId>("all");

  const containerRef = useRef<HTMLDivElement>(null);
  const previousQuotesRef = useRef<Quote[]>([]);
  const justSelectedTimestampRef = useRef<number | null>(null);

  const queries = {
    all: useFullTextAssetSearchInfinite({
      query,
      assetClass: undefined,
      exchanges: undefined,
    }),
    equities: useFullTextAssetSearchInfinite({
      query,
      assetClass: "us_equity",
      exchanges: undefined,
      enabled: showFilters,
    }),
    crypto: useFullTextAssetSearchInfinite({
      query,
      assetClass: "crypto",
      exchanges: undefined,
      enabled: showFilters,
    }),
  };

  const currentQuery = queries[activeTab];
  const activeAssets = (
    currentQuery.data?.pages.flatMap((page) =>
      Array.isArray(page) ? page : page.assets || [],
    ) || []
  ).filter((asset) => ALLOWED_ASSET_CLASSES.includes(asset.class));

  // Extract symbols and fetch quotes for the search results
  const symbols = useMemo(
    () => activeAssets.map((a) => a.symbol),
    [activeAssets],
  );

  // Search results contain mixed symbols — use default isOvernightTradable=true
  // so overnight-eligible symbols get the correct feed during overnight sessions.
  const { latestFeed } = useMarketDataFeed();

  const {
    quotes,
    isLoading: isQuoteLoading,
    isFetching: isQuoteFetching,
  } = useGetQuotes({
    symbols,
    feed: latestFeed,
    options: {
      enabled: symbols.length > 0,
      placeholderData: keepPreviousData,
    },
  });

  // Merge quote data with asset data
  // Preserve previous quotes to prevent data loss during refetches
  const allQuotes = useMemo(() => {
    const currentQuotes = quotes ?? [];
    const previousQuotes = previousQuotesRef.current;

    // Merge current and previous quotes, with current taking precedence
    const quoteMap = new Map<string, Quote>();
    previousQuotes.forEach((q) => {
      quoteMap.set(q.symbol, q);
    });
    currentQuotes.forEach((q) => {
      quoteMap.set(q.symbol, q);
    });

    const merged = Array.from(quoteMap.values());
    previousQuotesRef.current = merged;
    return merged;
  }, [quotes]);

  const enrichedAssets = useMemo(() => {
    const quoteMap = new Map(allQuotes.map((q) => [q.symbol, q]));
    return activeAssets.map((asset) => {
      const quote = quoteMap.get(asset.symbol);

      // Determine if this asset's quote is loading
      // Quote is loading if: quotes are loading AND this asset doesn't have a quote yet
      const assetQuoteLoading = (isQuoteLoading || isQuoteFetching) && !quote;

      const isCrypto = asset.class === "crypto";
      const showNoOvernightSupportIcon =
        !isCrypto &&
        asset.tradable &&
        !asset.attributes?.includes("overnight_tradable");
      const showOvernightHaltedIcon =
        !isCrypto &&
        asset.tradable &&
        !showNoOvernightSupportIcon &&
        (asset.attributes?.includes("overnight_halted") ?? false);

      // Only update quote fields when we have actual quote data
      if (quote) {
        const { price, dailyChange, dailyChangePercent } =
          getAssetQuotePricing(quote);

        return {
          ...asset,
          showOvernightHaltedIcon,
          showNoOvernightSupportIcon,
          price,
          dailyChange,
          dailyChangePercent,
          isQuoteLoading: false,
        };
      }

      // Preserve existing quote values or leave undefined when no quote exists
      return {
        ...asset,
        showOvernightHaltedIcon,
        showNoOvernightSupportIcon,
        isQuoteLoading: assetQuoteLoading,
      };
    });
  }, [activeAssets, allQuotes, isQuoteLoading, isQuoteFetching]);

  const tabsWithCounts = TAB_CONFIG.map((t) => {
    if (!showFilters) {
      return { ...t, count: 0 };
    }
    if (t.id === "all") {
      // TODO - handle searching for multiple asset classes in the backend
      // Sum equities + crypto counts for "all" tab since we filter out other asset classes client-side
      // Only show count once both queries have data to avoid flicker
      const equitiesData = queries.equities.data?.pages[0];
      const cryptoData = queries.crypto.data?.pages[0];
      if (!equitiesData || !cryptoData) {
        return { ...t, count: 0 };
      }
      return {
        ...t,
        count: (equitiesData.count ?? 0) + (cryptoData.count ?? 0),
      };
    }
    const firstPage = queries[t.id].data?.pages[0];
    const count = firstPage?.count ?? 0;
    return {
      ...t,
      count,
    };
  });

  const handleSelect = (asset: { symbol: string }) => {
    setIsOpen(false);
    justSelectedTimestampRef.current = Date.now();
    if (onSelect) {
      onSelect(asset.symbol);
    } else {
      history.push(Path.ROUTE_TRADE.replace(":symbol", asset.symbol));
    }
  };

  // Outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (val: string) => {
    if (!isControlled) {
      setInternalQuery(val);
    }
    onChange?.(val);
    if (val.trim()) {
      setIsOpen(true);
    }
  };

  const handleFocus = () => {
    if (
      justSelectedTimestampRef.current &&
      Date.now() - justSelectedTimestampRef.current < 500
    ) {
      justSelectedTimestampRef.current = null;
      return;
    }
    if (query.trim()) {
      setIsOpen(true);
    }
  };

  const handleTabChange = (id: string) => {
    setActiveTab(id as TabId);
  };

  return (
    <div ref={containerRef}>
      <AssetSearchPresenter
        query={query}
        onQueryChange={handleInputChange}
        onFocus={handleFocus}
        onClose={() => setIsOpen(false)}
        isOpen={isOpen && !!query}
        assets={enrichedAssets}
        tabs={tabsWithCounts}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isLoading={currentQuery.isLoading}
        isLoadingMore={currentQuery.isFetchingNextPage}
        hasMore={!!currentQuery.hasNextPage}
        onLoadMore={currentQuery.fetchNextPage}
        onSelect={handleSelect}
        placeholder={placeholder}
        autofocus={autofocus}
        showFilters={showFilters}
      />
    </div>
  );
};
